import { createColumnHelper, getCoreRowModel, RowSelectionState, useReactTable } from '@tanstack/react-table';
import React, { useCallback, useMemo, useState } from 'react';
import { TaskData } from '../../../api/endpoints/tasks';
import { Checkbox } from '../../../ui/Checkbox';
import { TaskBoardCell } from './TaskBoardCell';
import { TaskCreatedAtCell } from './TaskCreatedAtCell';
import { TaskDueDateCell } from './TaskDueDateCell';
import { TaskOwnerCell } from './TaskOwnerCell';
import { TaskStatusCell } from './TaskStatusCell';
import { TaskSummaryCell } from './TaskSummaryCell';

const columnHelper = createColumnHelper<TaskData>();

const useColumns = (readonly?: boolean) =>
    useMemo(() => [
        columnHelper.display({
            id: 'select',
            size: 0,
            header: ({ table }) => (
                <Checkbox
                    checked={
                        table.getIsAllPageRowsSelected()
                    }
                    onChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                />
            ),
            enableSorting: false,
            enableHiding: false,
        }),
        columnHelper.accessor(row => row, {
            id: 'summary',
            header: () => <span>Summary</span>,
            cell: info => <TaskSummaryCell readonly={readonly} task={info.getValue()} />,
        }),
        columnHelper.accessor(row => row, {
            id: 'status',
            size: 0,
            header: () => <span>Status</span>,
            cell: info => <TaskStatusCell readonly={readonly} task={info.getValue()} />,
        }),
        columnHelper.accessor(row => row, {
            id: 'userId',
            size: 0,
            header: () => <span>Owner</span>,
            cell: info => <TaskOwnerCell readonly={readonly} task={info.getValue()} />,
        }),
        columnHelper.accessor(row => row, {
            id: 'dueDate',
            size: 0,
            header: () => <span>Due</span>,
            cell: info => <TaskDueDateCell readonly={readonly} task={info.getValue()} />,
        }),
        columnHelper.accessor(row => row, {
            id: 'boardId',
            header: () => <span>Board</span>,
            cell: info => <TaskBoardCell task={info.getValue()} />,
        }),
        columnHelper.accessor(row => row, {
            id: 'createdAt',
            size: 0,
            header: () => <span>Created</span>,
            cell: info => <TaskCreatedAtCell task={info.getValue()} />,
        }),
    ], [readonly]);

export type TaskTableModelProps = {
    items: TaskData[],
    readonly?: boolean,
    onSortChange?: (sort: string) => void,
};

export const useTaskTableModel = ({ items, readonly, onSortChange }: TaskTableModelProps) => {

    const itemIds = new Set(items.map(x => x.id));
    const [selection, setSelection] = useState([]);

    const rowSelection = useMemo(() => {
        const lookup = new Set(selection.map(x => x.id));
        return Object.fromEntries(items.map((x, i) => [`${i}`, lookup.has(x.id)]).filter(([_, v]) => v));
    }, [items, selection]);

    const onRowSelectionChange = (updater: (old: RowSelectionState) => any) => {
        const nextSelection = new Set(Object.keys(updater(rowSelection)));
        const excluded = selection.filter(x => !itemIds.has(x.id));
        const included = items.filter((_, i) => nextSelection.has(`${i}`));
        setSelection([...excluded, ...included]);
    };

    const columns = useColumns(readonly);
    const table = useReactTable({
        columns,
        data: items,
        state: { rowSelection },
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        onRowSelectionChange,
        onStateChange(updater) {
            if (updater instanceof Function) {
                const { sorting } = updater(table.getState());
                if (sorting.length) {
                    onSortChange(`${sorting[0].id}:${sorting[0].desc ? 'desc' : 'asc'}`);
                }
            }
        },
    });

    return {
        table,
        selection,
        setSelection,
        clearSelection: useCallback(() => setSelection([]), []),
    };
};
